import React, { useMemo, useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/views/prepaidLoyalty/numbersTabStyle';
import { translate } from 'components/Translation';
import Table from 'components/CustomTable/CustomTable';
import TableContainer from '@material-ui/core/TableContainer';
import TableHeader from 'components/TableHeader/TableHeader';
import { Column } from 'react-table';
import { PhoneNumberCell, CheckboxCell } from 'components/Cells/Cells';
import { primaryColor } from 'assets/jss/material-dashboard-pro-react';
import TopUpCard from 'views/TopUpAddOns/commons/TopUpCard';
import { AccountType, BillingType } from 'utils/api/models/accounts/singleAccount';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { SingleLoyaltyNumber } from 'utils/api/models/prepaidLoyalty/singleLoyaltyNumber';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import AttentionCCLimitNote from '../attentionCCLimitNote';
import { config } from 'views/PrepaidLoyalty/config';
import { PAYMENT_METHODS } from '../constants';

// @ts-ignore
const useStyles = makeStyles(styles);

type INumbersTabProps = {
	numbersLoading: boolean;
	numbersData: SingleLoyaltyNumber[];
	selectedNumbers: SingleLoyaltyNumber[];
	topUpValue?: number;
	changeSelected: (number: SingleLoyaltyNumber) => void;
	isNumberSelected: (number: SingleLoyaltyNumber) => boolean;
	setTopUpValue?: React.Dispatch<React.SetStateAction<number>>;
	creditLimit?: string;
	setCreditLimit?: React.Dispatch<React.SetStateAction<string>>;
	paymentMethod?: number;
	setActiveTab?: React.Dispatch<React.SetStateAction<number>>;
	masterNumber?: string;
	accountDetails?: string;
	accountNumber?: string;
	userData?: CorpUserData | undefined;
};

const NumbersTab = ({
	numbersLoading,
	numbersData,
	selectedNumbers,
	topUpValue,
	changeSelected,
	isNumberSelected,
	setTopUpValue,
	creditLimit,
	setCreditLimit,
	paymentMethod,
	setActiveTab,
	masterNumber,
	accountDetails,
	accountNumber,
	userData,
}: INumbersTabProps) => {
	const classes = useStyles();
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [isEmpty, setIsEmpty] = useState<boolean>(false);
	const [customTopUpValue, setCustomTopUpValue] = useState<number>(0);
	const [selectedNumbersExceededLimit, setSelectedNumbersExceededLimit] = useState<boolean>(false);

	useEffect(() => {
		setSelectedNumbersExceededLimit(selectedNumbers?.length >= config.MAX_NUMBERS_CC_PAYMENT);
	}, [selectedNumbers]);

	const data = useMemo(() => {
		const _searchInputValue = searchInputValue.toLowerCase();
		return numbersData.filter((number: any) => {
			return [
				[
					_searchInputValue !== '',
					['number']
						.filter((field) => !!number[field])
						.some((field) => number[field].toLowerCase().includes(_searchInputValue)),
				],
			]
				.filter(([check, _]) => check)
				.every(([_, isTrue]) => isTrue);
		});
	}, [searchInputValue, numbersData]);

	const topUpSelectedNumbers: types.topUp.INumber[] = useMemo(
		() =>
			selectedNumbers.map((number: any) => ({
				accountNum: number,
				_isMain: '',
				isMain: false,
				accountId: '1',
				bAccountId: '1',
				createdAt: '1',
				activatedAt: '1',
				description: '1',
				upAccountId: '1',
				billingType: BillingType.POSTPAID,
				accountType: AccountType.MOBILE,
				accountPlan: '1',
				adslLogin: '1',
				isPrepaid: false,
				isPostpaid: true,
				id: 1,
				login: '1',
				userId: '1',
				userType: 'ENDUSER',
				deptId: '1',
				topUpValue: topUpValue ? topUpValue : 0,
			})),
		[selectedNumbers, topUpValue]
	); //added temporarily for API intergration

	const isTopUpMode: boolean = !!topUpValue && !!setTopUpValue;

	const totalAmount: number = useMemo(
		() =>
			topUpSelectedNumbers.reduce(
				(previous, { topUpValue }) => (topUpValue == null ? 0 : Number(topUpValue)) + previous,
				0
			),
		[topUpSelectedNumbers]
	);
	const closeEdit = () => {};

	const checkTopUpValue = (value: string, setIsEmpty: Function, setValue: (value: number) => void) => {
		if (value === '') {
			setIsEmpty(true);
		} else if (/[0-9]+/.test(value)) {
			const numberValue = parseInt(value);
			if (numberValue > 0 && numberValue <= 100) {
				setValue(numberValue);
				setIsEmpty(false);
			}
		}
	};

	const columns: any[] = useMemo(
		() =>
			[
				{
					minWidth: 130,
					Header: translate('prepaid.loyalty.mobile.number'),
					Cell: ({ original }: { original: SingleLoyaltyNumber }) => (
						<PhoneNumberCell text={original.number} />
					),
					accessor: 'number',
				},
				{
					Header: translate('prepaid.loyalty.selected.numbers', selectedNumbers.length, numbersData.length),
					Cell: ({ original }: { original: SingleLoyaltyNumber }) => {
						const checked = isNumberSelected(original);
						const text = checked ? translate('selected') : translate('select');
						return (
							<CheckboxCell
								text={text}
								checked={checked}
								onClick={() => {
									changeSelected(original);
								}}
								labelProps={{
									fontWeight: '600',
									color: primaryColor[0],
								}}
							/>
						);
					},
					minWidth: 150,
				},
			].filter(Boolean) as Column<any>[],
		[selectedNumbers, numbersData, changeSelected, isNumberSelected]
	);

	return (
		<>
			<ConditionalRender
				show={isTopUpMode}
				onTrue={() => (
					<TopUpCard
						isEmpty={isEmpty}
						setIsEmpty={setIsEmpty}
						topUpValue={topUpValue!}
						setTopUpValue={setTopUpValue!}
						customTopUpValue={customTopUpValue}
						selectedNumbers={topUpSelectedNumbers}
						totalAmount={totalAmount}
						closeEdit={closeEdit}
						setCustomTopUpValue={setCustomTopUpValue}
						checkTopUpValue={checkTopUpValue}
						creditLimit={creditLimit}
						setCreditLimit={setCreditLimit}
						paymentMethod={paymentMethod}
						setActiveTab={setActiveTab}
						masterNumber={masterNumber}
						accountDetails={accountDetails}
						accountNumber={accountNumber}
						userData={userData}
					/>
				)}
			/>
			<ConditionalRender
				show={paymentMethod === PAYMENT_METHODS.BY_CREDIT_CARD && selectedNumbersExceededLimit}
				onTrue={() => (
					<AttentionCCLimitNote
						body={<>{translate('prepaid.loyalty.pay.cc.limit', config.MAX_NUMBERS_CC_PAYMENT)} </>}
					/>
				)}
			/>
			<div className={classes.tab}>
				<div className={classes.searchWrapper}>
					<TableHeader
						label={translate('search')}
						setSearchInputValue={(value) => setSearchInputValue(value)}
						searchInputValue={searchInputValue}
					/>
				</div>
				<TableContainer className={classes.tableContainer}>
					<Table
						total={data.length}
						withHeader
						alignColumnsRight={[2]}
						tableProps={{
							loading: numbersLoading,
							data,
							defaultPageSize: numbersData.length > 5 ? 10 : 5,
							columns,
						}}
					/>
				</TableContainer>
			</div>
		</>
	);
};
export default NumbersTab;
